import { useState } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import merchant_services from "services/merchant_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import { apiErrorMessage } from "utils/errors/parseApiError";

export type MerchantStatusAction = "suspend" | "activate";

export interface UseMerchantStatusToggleReturn {
  changingMerchantStatus: boolean;
  changingMerchantStatusMsg: string;
  merchantStatusError: boolean;
  merchantStatusErrorMsg: string;
  merchantStatusSuccess: boolean;
  merchantStatusSuccessMsg: string;
  handleMerchantStatusChange: (
    action: MerchantStatusAction,
    merchantId: string,
  ) => Promise<void>;
  handleRetry: () => Promise<void>;
  handleCloseError: () => void;
  handleCloseSuccess: () => void;
}

/**
 * Suspend (Active -> Suspended) and its reverse, which setMerchantStatus allows only between
 * those two states — Blocked and Revoked are terminal, so activate can never un-block.
 */
export function useMerchantStatusToggle(): UseMerchantStatusToggleReturn {
  const networkType = useSelector(networkTypeSelector);
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [lastRequest, setLastRequest] = useState<{
    action: MerchantStatusAction;
    merchantId: string;
  } | null>(null);

  const openError = (msg: string) => {
    setLoadingMsg("");
    setIsLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMsg("");
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessMsg("");
  };

  const handleMerchantStatusChange = async (
    action: MerchantStatusAction,
    merchantId: string,
  ) => {
    const suspending = action === "suspend";
    setLastRequest({ action, merchantId });
    // ErrorModal's "Try again" does not dismiss itself, so a retry would stack the next modal
    // on top of the open error.
    handleCloseError();

    try {
      setLoadingMsg(
        suspending ? "Suspending merchant…" : "Activating merchant…",
      );
      setIsLoading(true);

      if (suspending) {
        await merchant_services.suspendMerchant(merchantId, networkType);
      } else {
        await merchant_services.activateMerchant(merchantId, networkType);
      }

      // The row reads `status` from the list query, so without this the badge — and this very
      // menu item — keep offering the action that just succeeded.
      queryClient.invalidateQueries({ queryKey: ["merchants"] });
      // Marked stale but NOT refetched: useMerchantLookup reports isFetching as "loading", which
      // swaps the searched row for a skeleton and unmounts the success modal below.
      queryClient.invalidateQueries({
        queryKey: ["merchant-lookup"],
      });
      queryClient.invalidateQueries({ queryKey: ["balances", "merchants"] });

      setIsLoading(false);
      setLoadingMsg("");
      setSuccessMsg(
        `Merchant ${merchantId} has been ${suspending ? "suspended" : "activated"}.`,
      );
      setShowSuccess(true);
    } catch (error: any) {
      openError(
        apiErrorMessage(
          error,
          suspending
            ? "Failed to suspend merchant. Please try again."
            : "Failed to activate merchant. Please try again.",
        ),
      );
    }
  };

  const handleRetry = () =>
    lastRequest
      ? handleMerchantStatusChange(lastRequest.action, lastRequest.merchantId)
      : Promise.resolve();

  return {
    changingMerchantStatus: isLoading,
    changingMerchantStatusMsg: loadingMsg,
    merchantStatusError: showError,
    merchantStatusErrorMsg: errorMsg,
    merchantStatusSuccess: showSuccess,
    merchantStatusSuccessMsg: successMsg,
    handleMerchantStatusChange,
    handleRetry,
    handleCloseError,
    handleCloseSuccess,
  };
}
